import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

const SECTIONS = ['hero', 'about', 'services', 'process', 'clients', 'faqs', 'contact'];
const SECTION_LABELS = {
  hero: '🏠 Hero', about: 'ℹ️ About', services: '⚙️ Services',
  process: '📋 Process', clients: '🤝 Clients', faqs: '❓ FAQs', contact: '📞 Contact',
};

export default function AdminContent() {
  const iframeRef   = useRef(null);
  const [ready,     setReady]     = useState(false);
  const [pending,   setPending]   = useState({});   // { section: sectionData }
  const [deploying, setDeploying] = useState(false);
  const [deployed,  setDeployed]  = useState(false);

  // Enable edit mode once iframe signals it's ready
  const enableEditMode = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'AMKO_EDIT_MODE' }, '*');
    setReady(true);
  }, []);

  // Listen for field changes coming back from the live site
  useEffect(() => {
    const handler = e => {
      if (e.data?.type === 'AMKO_READY') {
        enableEditMode();
      }
      if (e.data?.type === 'AMKO_SECTION_UPDATE') {
        const { section, data } = e.data;
        setPending(p => ({ ...p, [section]: data }));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [enableEditMode]);

  const dirtyCount = Object.keys(pending).length;

  const deployAll = async () => {
    if (deploying || dirtyCount === 0) return;
    setDeploying(true);
    try {
      await Promise.all(
        Object.entries(pending).map(([section, data]) =>
          setDoc(doc(db, 'siteContent', section), data)
        )
      );
      setPending({});
      setDeployed(true);
      setTimeout(() => setDeployed(false), 3500);
    } finally {
      setDeploying(false);
    }
  };

  const scrollTo = (section) => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'AMKO_SCROLL_TO', section },
      '*'
    );
  };

  return (
    <div className="ce-live-root">

      {/* Floating toolbar */}
      <div className="ce-live-toolbar">
        <div className="ce-live-toolbar-left">
          <span className="ce-live-indicator">
            <span className={`ce-live-dot ${ready ? '' : 'ce-live-dot-loading'}`} />
            {ready ? 'Editing Live' : 'Connecting…'}
          </span>
          {SECTIONS.map(s => (
            <button
              key={s}
              className={`ce-jump-btn ${pending[s] ? 'ce-jump-btn-dirty' : ''}`}
              onClick={() => scrollTo(s)}
            >
              {SECTION_LABELS[s]}{pending[s] ? ' ●' : ''}
            </button>
          ))}
        </div>
        <div className="ce-live-toolbar-right">
          {deployed && <span className="ce-deployed-badge">✓ Live!</span>}
          {dirtyCount > 0 && !deploying && (
            <span className="ce-unsaved-count">{dirtyCount} unsaved</span>
          )}
          <button
            className={`ce-deploy-btn ${deploying ? 'ce-deploy-btn-busy' : ''} ${dirtyCount === 0 && !deploying ? 'ce-deploy-btn-idle' : ''}`}
            onClick={deployAll}
            disabled={deploying || dirtyCount === 0}
          >
            {deploying ? '⏳ Deploying…' : '🚀 Deploy'}
          </button>
        </div>
      </div>

      {/* Live site iframe — full height */}
      <iframe
        ref={iframeRef}
        src="https://amkoiq.com"
        title="Live Site Editor"
        className="ce-live-iframe"
        onLoad={enableEditMode}
        allow="same-origin"
      />
    </div>
  );
}
